import { Component, h, Prop, Host, Watch, State } from '@stencil/core';
import { ProxioPriceGroupi18n, ProxioProduct, ProductGenericItem, ProductWeightGroupListItem, ProxioPriceListGroupResponse } from '../pn-proxio-pricegroup/types';
import { translations } from '../pn-proxio-pricegroup/translations';

@Component({
  tag: 'pn-proxio-findprice-result',
  styleUrl: 'pn-proxio-findprice-result.scss',
})
export class PnProxioFindPriceResult {
  @Prop({ mutable: true }) item: ProxioPriceListGroupResponse = null;
  @Prop({ mutable: true }) activeweight: number = null;
  @Prop() shownitems: ProxioPriceListGroupResponse[] = [];
  @Prop() Usp1?: string;
  @Prop() Usp2?: string;
  @Prop() Usp3?: string;
  @Prop() description?: string[] = [];
  @Prop() showMeasurement?: boolean = false;
  @Prop() showInternational: boolean = false;
  @Prop() selectedCountrycode: string = '';
  @Prop() market: string = '';
  @Prop({ mutable: true }) language: string = 'sv';

  @State() weightText = '';
  @State() linkId: string = '';
  @State() shopLabel: string = '';
  @State() shopUrl: string = '';
  @State() shopId: string = '';
  @State() i18n: ProxioPriceGroupi18n = translations[this.language];

  componentWillLoad() {
    this.composeItems();
  }

  @Watch('item')
  composeItems() {
    this.shownitems = [this.item];
  }

  @Watch('activeweight')
  getPriceString(product: ProxioProduct) {
    if (!product.productWeightGroupList) {
      return null;
    }

    const activeIndex = product.productWeightGroupList.findIndex(item => item.item1 === this.activeweight);

    let price = '';

    if (this.showInternational && this.selectedCountrycode && this.selectedCountrycode !== this.market && product.weightPriceCountryList) {
      let weightPriceCountryList = product.weightPriceCountryList.filter(item => item.item3.includes(this.selectedCountrycode.toUpperCase()));

      price = weightPriceCountryList ? weightPriceCountryList[activeIndex]?.item2 : '';
    } else {
      price = product.weightAndPriceList ? product?.weightAndPriceList[activeIndex]?.item2 : '';
    }

    return price ? this.i18n.from + price : '';
  }

  @Watch('activeweight')
  getProductLink(product: ProxioProduct, productWeightGroupList: ProductWeightGroupListItem[]) {
    if (product.productWeightGroupList) {
      const activeIndex = product.productWeightGroupList.findIndex(item => item.item1 === this.activeweight);

      let productId = productWeightGroupList[activeIndex]?.item2;
      let productLink = product.productLink ? product?.productLink : '';
      return productLink ? productLink + (productId ? '?productId=' + productId : '') : '';
    }
    return null;
  }

  getDeliveryInformation(product: ProxioProduct) {
    if (!this.showInternational || !product.deliveryDateInternationalInformation) {
      return product.deliveryDateInformation;
    }

    const internationalShipment = product?.deliveryDateInternationalInformation?.find(info => info.item1.toLowerCase() === this.selectedCountrycode.toLowerCase());

    if (!internationalShipment?.item2 ?? true) {
      return product.deliveryDateInformation;
    }

    return internationalShipment.item2;
  }

  getInsuranceAmountText(product: ProxioProduct) {
    if (!product.insuranceAmounts) {
      return '';
    }

    const insuranceAmount = product?.insuranceAmounts?.find(info => info.item1.toLowerCase() === this.selectedCountrycode.toLowerCase());

    if (!insuranceAmount?.item2 ?? true) {
      return '';
    }

    return insuranceAmount.item2;
  }

  render() {
    return (
      <Host>
        {
          <pn-proxio-productcard>
            <pn-illustration slot="illustrationmobile" illustration={this.item.proxioProduct.productIcon}></pn-illustration>
            <h2 slot="title">{this.item.proxioProduct.productTitle}</h2>
            <pn-proxio-productcard-description
              slot="description"
              heading={this.item.proxioProduct.productPreamble}
              highlight={this.getDeliveryInformation(this.item.proxioProduct)}
              list={this.item.proxioProduct.productDescriptionBulletListItems}
            ></pn-proxio-productcard-description>
            <div slot="information">
              <pn-proxio-productcard-information
                label={this.i18n.labelMaxDimensions}
                information={this.item.proxioProduct.productPackageDimensionsList as ProductGenericItem[]}
              ></pn-proxio-productcard-information>
              <pn-proxio-productcard-information
                label={this.i18n.labelMaxDimensionsRoll}
                information={this.item.proxioProduct.productRollDimensionsList as ProductGenericItem[]}
              ></pn-proxio-productcard-information>
            </div>
            <div slot="bottomtags">
              {this.item.proxioProduct.usp1 ? <pn-titletag color="gray50">{this.item.proxioProduct.usp1}</pn-titletag> : null}
              {this.item.proxioProduct.usp2 ? <pn-titletag color="gray50">{this.item.proxioProduct.usp2}</pn-titletag> : null}
              {this.item.proxioProduct.usp3 ? <pn-titletag color="gray50">{this.item.proxioProduct.usp3}</pn-titletag> : null}
            </div>
            <pn-illustration slot="illustration" illustration={this.item.proxioProduct.productIcon}></pn-illustration>
            <div slot="pricelink">
              <pn-proxio-productcard-pricelink
                price={this.getPriceString(this.item.proxioProduct)}
                link={this.getProductLink(this.item.proxioProduct, this.item.proxioProduct.productWeightGroupList)}
                linktext={this.item.proxioProduct.productLinkText}
              ></pn-proxio-productcard-pricelink>
              {this.item.relatedProxioProducts
                ? this.item.relatedProxioProducts.map(product => (
                    <pn-proxio-productcard-pricelink
                      price={this.getPriceString(product)}
                      link={product.productLink}
                      linktext={product.productLinkText}
                    ></pn-proxio-productcard-pricelink>
                  ))
                : null}
            </div>
            {this.getInsuranceAmountText(this.item.proxioProduct) ? (
              <div slot="insurance">
                <div class="insurance-box">{this.getInsuranceAmountText(this.item.proxioProduct)}</div>
              </div>
            ) : null}
          </pn-proxio-productcard>
        }
      </Host>
    );
  }
}
